import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Logo, Brand } from "@/components/Logo";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Search, Crown, Shield, ShieldAlert, AlertTriangle, User as UserIcon, Loader2, ChevronDown, Clock, Trash2,
  Users, UserPlus, Activity, MessageSquare, MessagesSquare, DollarSign, TrendingUp,
  Mail, Copy, Check, Link2, LifeBuoy, Flag, KeyRound, Eye, EyeOff, RefreshCw, Megaphone, Send, FileText, Plus, Pencil, X, Pilcrow, Bold, Image as ImageIcon, Heading } from "lucide-react";
import { parseContent, renderInline } from "@/lib/articleFormat";

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className={`rounded-xl p-4 border ${accent ? "bg-[#FF5A00]/10 border-[#FF5A00]/30" : "bg-[#15151d] border-[#2a3040]"}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#6b7280] font-bold mb-2">
        <Icon size={13} className="text-[#FF5A00]" /> {label}
      </div>
      <div className="font-display font-black text-2xl text-white leading-none">{value}</div>
      {sub && <div className="text-[11px] text-[#9ca3af] mt-1.5">{sub}</div>}
    </div>
  );
}

const DURATIONS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "1 year", days: 365 },
  { label: "Permanent", days: null },
];

function expiryLabel(iso) {
  if (!iso) return "Permanent";
  const end = new Date(iso);
  const days = Math.ceil((end - new Date()) / 86400000);
  if (days <= 0) return "Expired";
  return `Expires in ${days} day${days === 1 ? "" : "s"}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

function RoleBadge({ role }) {
  const map = {
    owner: { label: "Owner", cls: "bg-[#FF5A00] text-[#0a0a0f]", icon: Crown },
    admin: { label: "Admin", cls: "bg-[#2a3040] text-white", icon: Shield },
    user: { label: "User", cls: "bg-[#1a1a24] text-[#9ca3af]", icon: UserIcon },
  };
  const r = map[role] || map.user;
  const Icon = r.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold ${r.cls}`}>
      <Icon size={9} /> {r.label}
    </span>
  );
}

function InviteSection() {
  const [emails, setEmails] = useState("");
  const [grantPro, setGrantPro] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState([]);
  const [copiedToken, setCopiedToken] = useState(null);
  const [invites, setInvites] = useState([]);
  const [revokingId, setRevokingId] = useState(null);
  const [linkResult, setLinkResult] = useState(null);
  const [creatingLink, setCreatingLink] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const inviteUrl = (token) => `${window.location.origin}/join/${token}`;

  const loadInvites = useCallback(() => {
    api.get("/admin/invites").then((r) => setInvites(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadInvites(); }, [loadInvites]);

  const generate = async () => {
    if (!emails.trim()) { toast.error("Enter at least one email address"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/invites", { emails, grant_pro: grantPro });
      setResults(data.invites);
      toast.success(`Generated ${data.invites.length} invite link${data.invites.length === 1 ? "" : "s"}`);
      setEmails("");
      loadInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate invites");
    } finally {
      setGenerating(false);
    }
  };

  const revoke = async (inv) => {
    setRevokingId(inv.id);
    try {
      await api.delete(`/admin/invites/${inv.id}`);
      setInvites((prev) => prev.filter((x) => x.id !== inv.id));
      if (linkResult && inv.token === linkResult.token) setLinkResult(null);
      toast.success(inv.kind === "link" ? "Free Pro link disabled" : `Removed invite · ${inv.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not revoke invite");
    } finally {
      setRevokingId(null);
    }
  };

  const generateLink = async () => {
    setCreatingLink(true);
    try {
      const { data } = await api.post("/admin/invite-link", { max_uses: 25, days: 30, grant_pro: true });
      setLinkResult(data);
      loadInvites();
      toast.success("Free Pro link generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate link");
    } finally {
      setCreatingLink(false);
    }
  };

  const copyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(token));
      setCopiedLink(true);
      toast.success("Link copied — paste it into a text or message");
      setTimeout(() => setCopiedLink(false), 2000);
    } catch { toast.error("Copy failed — select and copy the link manually"); }
  };

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(token));
      setCopiedToken(token);
      toast.success("Invite link copied");
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      toast.error("Copy failed — select and copy the link manually");
    }
  };

  const copyAll = async () => {
    const text = results.map((r) => `${r.email}: ${inviteUrl(r.token)}`).join("\n");
    try { await navigator.clipboard.writeText(text); toast.success("All invite links copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="mb-10 bg-[#15151d] border border-[#2a3040] rounded-xl p-5" data-testid="invite-section">
      <h2 className="font-display font-black text-2xl tracking-tight mb-1 flex items-center gap-2">
        <Mail size={20} className="text-[#FF5A00]" /> Invite members
      </h2>
      <p className="text-[#9ca3af] text-sm mb-4">
        Paste one or more email addresses (comma or new line separated). We'll generate shareable links you can send yourself.
      </p>

      {/* Reusable Free Pro Membership link */}
      <div className="mb-6 rounded-lg border border-[#FF5A00]/30 bg-[#FF5A00]/[0.06] p-4" data-testid="pro-link-card">
        <div className="mb-1" data-testid="pro-link-heading">
          <div className="font-bold text-white"><Brand /> AI for Us</div>
          <div className="font-bold text-white">Free Pro Membership Link</div>
        </div>
        <p className="text-[#9ca3af] text-sm mb-3">
          One reusable link you can text or share anywhere. Grants free Pro to up to 25 people, expires in 30 days.
        </p>
        {!linkResult ? (
          <button data-testid="generate-pro-link-button" onClick={generateLink} disabled={creatingLink}
            className="inline-flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-5 py-2.5 hover:bg-[#FF7A33] transition-colors disabled:opacity-60">
            {creatingLink ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />}
            Generate Free Pro link
          </button>
        ) : (
          <div className="flex items-center gap-2 bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-3 py-2" data-testid="pro-link-result">
            <div className="min-w-0 flex-1">
              <div className="text-[13px] text-white truncate">{inviteUrl(linkResult.token)}</div>
              <div className="text-[11px] text-[#6b7280]">
                Up to {linkResult.max_uses} signups · {linkResult.expires_at ? `expires ${fmtDate(linkResult.expires_at)}` : "no expiry"}
              </div>
            </div>
            <button data-testid="copy-pro-link" onClick={() => copyLink(linkResult.token)}
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF5A00] hover:text-[#FF7A33] px-2 py-1">
              {copiedLink ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy</>}
            </button>
          </div>
        )}
      </div>

      <p className="text-xs uppercase tracking-wider text-[#6b7280] font-bold mb-3">Or invite specific people by email</p>
      <textarea data-testid="invite-emails-input" value={emails} onChange={(e) => setEmails(e.target.value)}
        rows={3} placeholder={"friend@example.com, another@example.com\nteammate@example.com"}
        className="w-full bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-4 py-3 text-white placeholder:text-[#6b7280] focus:outline-none focus:border-[#FF5A00] resize-y mb-3" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" data-testid="invite-grantpro-toggle" onClick={() => setGrantPro((v) => !v)}
          className={`inline-flex items-center gap-2 text-sm rounded-lg px-3 py-2 border transition-colors ${
            grantPro ? "bg-[#FF5A00]/15 border-[#FF5A00]/40 text-[#FF5A00]" : "bg-[#0a0a0f] border-[#2a3040] text-[#9ca3af]"}`}>
          <Crown size={15} /> {grantPro ? "Pro granted on signup" : "Grant Pro on signup"}
        </button>
        <button data-testid="invite-generate-button" onClick={generate} disabled={generating}
          className="inline-flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-5 py-2.5 hover:bg-[#FF7A33] transition-colors disabled:opacity-60">
          {generating ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />}
          Generate invite links
        </button>
      </div>

      {results.length > 0 && (
        <div className="mt-5 border-t border-[#2a3040] pt-4" data-testid="invite-results">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider text-[#6b7280] font-bold">Generated links</p>
            <button onClick={copyAll} data-testid="invite-copy-all" className="text-xs text-[#FF5A00] hover:text-[#FF7A33] font-semibold">Copy all</button>
          </div>
          <div className="space-y-2">
            {results.map((r) => (
              <div key={r.token} data-testid={`invite-result-${r.email}`}
                className="flex items-center gap-2 bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate flex items-center gap-2">
                    {r.email}
                    {r.grant_pro && <span className="text-[9px] uppercase tracking-wide bg-[#FF5A00]/15 text-[#FF5A00] px-1.5 py-0.5 rounded font-bold">Pro</span>}
                    {r.already_member && <span className="text-[9px] uppercase tracking-wide bg-[#2a3040] text-[#9ca3af] px-1.5 py-0.5 rounded font-bold">Already a member</span>}
                  </div>
                  <div className="text-[11px] text-[#6b7280] truncate">{inviteUrl(r.token)}</div>
                </div>
                <button data-testid={`invite-copy-${r.email}`} onClick={() => copy(r.token)}
                  className="shrink-0 text-[#9ca3af] hover:text-[#FF5A00] p-1.5">
                  {copiedToken === r.token ? <Check size={16} className="text-[#FF5A00]" /> : <Copy size={16} />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SupportInbox() {
  const [msgs, setMsgs] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/contact").then((r) => setMsgs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (m) => {
    setBusyId(m.id);
    try { await api.patch(`/admin/contact/${m.id}`); setMsgs((p) => p.map((x) => x.id === m.id ? { ...x, resolved: true } : x)); }
    catch { toast.error("Could not update"); } finally { setBusyId(null); }
  };
  const remove = async (m) => {
    setBusyId(m.id);
    try { await api.delete(`/admin/contact/${m.id}`); setMsgs((p) => p.filter((x) => x.id !== m.id)); toast.success("Message deleted"); }
    catch { toast.error("Could not delete"); } finally { setBusyId(null); }
  };

  const open = msgs.filter((m) => !m.resolved).length;

  return (
    <div className="mb-10 bg-[#15151d] border border-[#2a3040] rounded-xl p-5" data-testid="support-inbox">
      <h2 className="font-display font-black text-2xl tracking-tight mb-1 flex items-center gap-2">
        <LifeBuoy size={20} className="text-[#FF5A00]" /> Support inbox
        {open > 0 && <span className="text-[10px] uppercase tracking-wide bg-[#FF5A00] text-[#0a0a0f] px-2 py-0.5 rounded-full font-bold">{open} new</span>}
      </h2>
      <p className="text-[#9ca3af] text-sm mb-4">Messages sent from the Contact &amp; Support form in the app.</p>
      {msgs.length === 0 ? (
        <p className="text-sm text-[#6b7280]">No messages yet.</p>
      ) : (
        <div className="space-y-2">
          {msgs.map((m) => (
            <div key={m.id} data-testid={`support-msg-${m.id}`}
              className={`rounded-lg border p-3 ${m.resolved ? "border-[#2a3040] bg-[#0a0a0f] opacity-70" : "border-[#FF5A00]/25 bg-[#0a0a0f]"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm text-white font-semibold truncate">{m.subject}</div>
                  <div className="text-[11px] text-[#6b7280] truncate">
                    {m.name || "Member"} · <a href={`mailto:${m.email}`} className="text-[#FF5A00] hover:underline">{m.email}</a> · {fmtDate(m.created_at)}
                    {m.resolved && <span className="ml-2 text-[#4ade80]">Resolved</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!m.resolved && (
                    <button data-testid={`support-resolve-${m.id}`} onClick={() => resolve(m)} disabled={busyId === m.id}
                      className="text-[#9ca3af] hover:text-[#4ade80] p-1.5" title="Mark resolved">
                      {busyId === m.id ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                    </button>
                  )}
                  <button data-testid={`support-delete-${m.id}`} onClick={() => remove(m)} disabled={busyId === m.id}
                    className="text-[#6b7280] hover:text-[#f87171] p-1.5" title="Delete">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <p className="text-sm text-[#c7ccd6] mt-2 whitespace-pre-wrap break-words">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CommunityPromptSetter() {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/community/prompt").then((r) => setText(r.data.text || "")).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/community/prompt", { text }); toast.success("Weekly prompt updated"); }
    catch { toast.error("Could not save prompt"); } finally { setSaving(false); }
  };

  return (
    <div className="mb-10 bg-[#15151d] border border-[#2a3040] rounded-xl p-5" data-testid="community-prompt-setter">
      <h2 className="font-display font-black text-2xl tracking-tight mb-1 flex items-center gap-2">
        <Users size={20} className="text-[#FF5A00]" /> Community prompt
      </h2>
      <p className="text-[#9ca3af] text-sm mb-3">The weekly question pinned at the top of the Community page. Leave blank to hide it.</p>
      <textarea data-testid="community-prompt-input" value={text} onChange={(e) => setText(e.target.value)}
        rows={2} placeholder="e.g. What's one piece of wisdom an elder gave you that changed your life?"
        className="w-full bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-4 py-3 text-white placeholder:text-[#6b7280] focus:outline-none focus:border-[#FF5A00] resize-y mb-3" />
      <button data-testid="community-prompt-save" onClick={save} disabled={saving}
        className="inline-flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-5 py-2.5 hover:bg-[#FF7A33] transition-colors disabled:opacity-60">
        {saving ? <Loader2 size={16} className="animate-spin" /> : null} Save prompt
      </button>
    </div>
  );
}

function ReportedPosts() {
  const [reports, setReports] = useState([]);
  const load = useCallback(() => { api.get("/admin/community/reports").then((r) => setReports(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const removePost = async (pid) => {
    try { await api.delete(`/admin/community/posts/${pid}`); setReports((p) => p.filter((x) => x.post_id !== pid)); toast.success("Post removed"); }
    catch { toast.error("Could not remove"); }
  };
  const dismiss = async (pid) => {
    try { await api.post(`/admin/community/reports/${pid}/dismiss`); setReports((p) => p.filter((x) => x.post_id !== pid)); toast.success("Reports dismissed"); }
    catch { toast.error("Could not dismiss"); }
  };

  return (
    <div className="mb-10 bg-[#15151d] border border-[#2a3040] rounded-xl p-5" data-testid="reported-posts">
      <h2 className="font-display font-black text-2xl tracking-tight mb-1 flex items-center gap-2">
        <Flag size={20} className="text-[#FF5A00]" /> Reported posts
        {reports.length > 0 && <span className="text-[10px] uppercase tracking-wide bg-[#FF5A00] text-[#0a0a0f] px-2 py-0.5 rounded-full font-bold">{reports.length}</span>}
      </h2>
      <p className="text-[#9ca3af] text-sm mb-4">Community posts flagged by members. Remove the post or dismiss the reports.</p>
      {reports.length === 0 ? (
        <p className="text-sm text-[#6b7280]">No reported posts. 🎉</p>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.post_id} className="rounded-lg border border-[#FF5A00]/25 bg-[#0a0a0f] p-3" data-testid={`report-${r.post_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[11px] text-[#6b7280] mb-1">{r.count} report{r.count === 1 ? "" : "s"} · {r.post_author || "—"}</div>
                  <p className="text-sm text-[#c7ccd6] whitespace-pre-wrap break-words">{r.post_content}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.post_exists && (
                    <button data-testid={`report-remove-${r.post_id}`} onClick={() => removePost(r.post_id)}
                      className="text-xs text-[#f87171] hover:underline">Remove post</button>
                  )}
                  <button data-testid={`report-dismiss-${r.post_id}`} onClick={() => dismiss(r.post_id)}
                    className="text-xs text-[#9ca3af] hover:text-white">Dismiss</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ArticlesSection() {
  const empty = { title: "", category: "Community & Business", excerpt: "", content: "", published: true, cover_image_url: "" };
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null=closed, {}=new, {id}=edit
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const contentRef = useRef(null);
  const coverRef = useRef(null);

  // Toggle a heading marker ("## " or "### ") at the start of the current line.
  const toggleHeading = (marker = "## ") => {
    const ta = contentRef.current;
    if (!ta) return;
    const val = form.content;
    const pos = ta.selectionStart;
    const lineStart = val.lastIndexOf("\n", pos - 1) + 1;
    const nl = val.indexOf("\n", pos);
    const lineEnd = nl === -1 ? val.length : nl;
    const line = val.slice(lineStart, lineEnd);
    const m = line.match(/^(#{1,3})\s+/);
    let newLine;
    if (m) {
      const current = m[1] + " ";
      newLine = current === marker ? line.replace(/^#{1,3}\s+/, "") : marker + line.replace(/^#{1,3}\s+/, "");
    } else {
      newLine = marker + line;
    }
    const newVal = val.slice(0, lineStart) + newLine + val.slice(lineEnd);
    setForm({ ...form, content: newVal });
    const delta = newLine.length - line.length;
    setTimeout(() => { ta.focus(); ta.setSelectionRange(pos + delta, pos + delta); }, 0);
  };

  // Wrap the current selection in **bold** (or insert a bold placeholder).
  const wrapBold = () => {
    const ta = contentRef.current;
    if (!ta) return;
    const val = form.content;
    const s = ta.selectionStart, e = ta.selectionEnd;
    const sel = val.slice(s, e) || "bold text";
    const newVal = val.slice(0, s) + "**" + sel + "**" + val.slice(e);
    setForm({ ...form, content: newVal });
    setTimeout(() => { ta.focus(); ta.setSelectionRange(s + 2, s + 2 + sel.length); }, 0);
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/articles/cover", fd);
      setForm((f) => ({ ...f, cover_image_url: r.data.url }));
      toast.success("Cover image uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not upload image"); }
    finally { setUploadingCover(false); }
  };

  const coverSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

  const load = useCallback(() => { api.get("/admin/articles").then((r) => setList(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setPreview(false); setEditing({}); };
  const openEdit = (a) => { setForm({ title: a.title, category: a.category, excerpt: a.excerpt, content: a.content, published: a.published, cover_image_url: a.cover_image_url || "" }); setPreview(false); setEditing(a); };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Add a title and content"); return; }
    setSaving(true);
    try {
      if (editing && editing.id) await api.put(`/admin/articles/${editing.id}`, form);
      else await api.post("/admin/articles", form);
      toast.success("Article saved");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/articles/${id}`); setList((p) => p.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const inputCls = "w-full bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-4 py-2.5 text-white placeholder:text-[#6b7280] focus:outline-none focus:border-[#FF5A00] transition-colors";

  return (
    <div className="mb-10 bg-[#15151d] border border-[#2a3040] rounded-xl p-5" data-testid="articles-admin-section">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
          <FileText size={20} className="text-[#FF5A00]" /> Articles
        </h2>
        {!editing && (
          <button data-testid="article-new" onClick={openNew}
            className="inline-flex items-center gap-1.5 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-3.5 py-2 text-sm hover:bg-[#FF7A33] transition-colors">
            <Plus size={15} /> New article
          </button>
        )}
      </div>
      <p className="text-[#9ca3af] text-sm mb-4">Publish public guides (readable without login) to grow SEO and support AdSense.</p>

      {editing ? (
        <div className="space-y-3" data-testid="article-editor">
          <input data-testid="article-title" className={inputCls} placeholder="Article title"
            value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={160} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="article-category" className={inputCls} placeholder="Category (e.g. Health & Wellness)"
              value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-[#c7ccd6] px-1">
              <input type="checkbox" data-testid="article-published" checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-[#FF5A00] w-4 h-4" />
              Published (visible to public)
            </label>
          </div>
          <input data-testid="article-excerpt" className={inputCls} placeholder="BY Line"
            value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} maxLength={400} />

          {/* Cover image */}
          <div className="rounded-lg border border-[#2a3040] bg-[#0a0a0f] p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-[#c7ccd6] flex items-center gap-1.5"><ImageIcon size={14} className="text-[#FF5A00]" /> Cover image</span>
              <span className="text-[11px] text-[#6b7280]">Shown atop the article & used for social sharing</span>
            </div>
            <input ref={coverRef} type="file" accept="image/*" className="hidden"
              data-testid="article-cover-input" onChange={(e) => { uploadCover(e.target.files?.[0]); e.target.value = ""; }} />
            {form.cover_image_url ? (
              <div className="flex items-center gap-3">
                <img src={coverSrc(form.cover_image_url)} alt="cover" data-testid="article-cover-preview"
                  className="h-16 w-28 object-cover rounded-md border border-[#2a3040]" />
                <button type="button" data-testid="article-cover-replace" onClick={() => coverRef.current?.click()}
                  className="text-xs font-bold text-[#c7ccd6] hover:text-white">Replace</button>
                <button type="button" data-testid="article-cover-remove" onClick={() => setForm({ ...form, cover_image_url: "" })}
                  className="text-xs font-bold text-[#f87171] hover:text-[#fca5a5]">Remove</button>
              </div>
            ) : (
              <button type="button" data-testid="article-cover-upload" onClick={() => coverRef.current?.click()} disabled={uploadingCover}
                className="inline-flex items-center gap-1.5 bg-[#15151d] border border-[#2a3040] text-[#c7ccd6] rounded-lg px-3 py-1.5 text-xs font-bold hover:border-[#FF5A00] hover:text-white transition-colors disabled:opacity-60">
                {uploadingCover ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} className="text-[#FF5A00]" />}
                {uploadingCover ? "Uploading…" : "Upload cover image"}
              </button>
            )}
          </div>

          {/* Formatting toolbar */}
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" data-testid="article-insert-heading" onClick={() => toggleHeading("## ")}
              className="inline-flex items-center gap-1.5 bg-[#0a0a0f] border border-[#2a3040] text-[#c7ccd6] rounded-lg px-3 py-1.5 text-xs font-bold hover:border-[#FF5A00] hover:text-white transition-colors">
              <Heading size={14} className="text-[#FF5A00]" /> Heading
            </button>
            <button type="button" data-testid="article-insert-subheading" onClick={() => toggleHeading("### ")}
              className="inline-flex items-center gap-1.5 bg-[#0a0a0f] border border-[#2a3040] text-[#c7ccd6] rounded-lg px-3 py-1.5 text-xs font-bold hover:border-[#FF5A00] hover:text-white transition-colors">
              <Pilcrow size={14} className="text-[#FF5A00]" /> Subheading
            </button>
            <button type="button" data-testid="article-bold" onClick={wrapBold}
              className="inline-flex items-center gap-1.5 bg-[#0a0a0f] border border-[#2a3040] text-[#c7ccd6] rounded-lg px-3 py-1.5 text-xs font-bold hover:border-[#FF5A00] hover:text-white transition-colors">
              <Bold size={14} className="text-[#FF5A00]" /> Bold
            </button>
            <button type="button" data-testid="article-preview-toggle" onClick={() => setPreview((p) => !p)}
              className="ml-auto inline-flex items-center gap-1.5 bg-[#0a0a0f] border border-[#2a3040] text-[#c7ccd6] rounded-lg px-3 py-1.5 text-xs font-bold hover:border-[#FF5A00] hover:text-white transition-colors">
              {preview ? <><Pencil size={14} className="text-[#FF5A00]" /> Edit</> : <><Eye size={14} className="text-[#FF5A00]" /> Preview</>}
            </button>
          </div>

          {preview ? (
            <div data-testid="article-preview" className="rounded-lg border border-[#2a3040] bg-[#0a0a0f] p-5 max-h-[520px] overflow-y-auto">
              {form.cover_image_url && (
                <img src={coverSrc(form.cover_image_url)} alt={form.title} className="w-full rounded-xl mb-5 border border-[#2a3040] object-cover max-h-[320px]" />
              )}
              {form.title && <h1 className="font-display font-black text-2xl sm:text-3xl text-white mb-3">{form.title}</h1>}
              {parseContent(form.content).map((s, i) => (
                <div key={i}>
                  {s.heading && (s.level === 3
                    ? <h3 className="font-display font-bold text-lg text-white mt-5 mb-2">{renderInline(s.heading)}</h3>
                    : <h2 className="font-display font-bold text-xl text-white mt-6 mb-2">{renderInline(s.heading)}</h2>)}
                  {s.body.map((p, j) => (
                    <p key={j} className="text-[15px] text-[#c7ccd6] leading-[1.8] mb-3">{renderInline(p)}</p>
                  ))}
                </div>
              ))}
              {!form.content.trim() && <p className="text-sm text-[#6b7280]">Nothing to preview yet — write some content.</p>}
            </div>
          ) : (
            <textarea ref={contentRef} data-testid="article-content" className={`${inputCls} font-mono text-sm`} rows={12}
              placeholder={"Write your article here.\n\nUse Heading / Subheading on any line, select text and tap Bold, then write paragraphs below.\n\nLeave a blank line between paragraphs."}
              value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
          )}
          <p className="text-[11px] text-[#6b7280]">Tip: <span className="text-[#FF5A00] font-bold">Heading</span>/<span className="text-[#FF5A00] font-bold">Subheading</span> format the current line; <span className="text-[#FF5A00] font-bold">Bold</span> wraps the selected text. Tap <span className="text-[#FF5A00] font-bold">Preview</span> to see it formatted.</p>
          <div className="flex gap-2">
            <button data-testid="article-save" onClick={save} disabled={saving}
              className="inline-flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-5 py-2.5 hover:bg-[#FF7A33] transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {saving ? "Saving…" : "Save article"}
            </button>
            <button data-testid="article-cancel" onClick={() => setEditing(null)}
              className="inline-flex items-center gap-1.5 bg-[#1a1a24] border border-[#2a3040] text-white rounded-lg px-4 py-2.5 hover:bg-[#22222c] transition-colors">
              <X size={15} /> Cancel
            </button>
          </div>
        </div>
      ) : list.length === 0 ? (
        <p className="text-sm text-[#6b7280]">No custom articles yet. Your 5 starter guides are already live; add more here.</p>
      ) : (
        <div className="space-y-2">
          {list.map((a) => (
            <div key={a.id} className="rounded-lg border border-[#2a3040] bg-[#0a0a0f] p-3 flex items-start justify-between gap-2" data-testid={`article-row-${a.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-bold text-white flex items-center gap-2">
                  {a.title}
                  {!a.published && <span className="text-[10px] uppercase text-[#6b7280] border border-[#2a3040] rounded px-1.5 py-0.5">Draft</span>}
                </div>
                <div className="text-[11px] text-[#6b7280] mt-0.5">{a.category} · /articles/{a.slug}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button data-testid={`article-edit-${a.id}`} onClick={() => openEdit(a)} className="p-1.5 text-[#9ca3af] hover:text-[#FF5A00]" title="Edit"><Pencil size={15} /></button>
                <button data-testid={`article-delete-${a.id}`} onClick={() => remove(a.id)} className="p-1.5 text-[#9ca3af] hover:text-[#f87171]" title="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BroadcastSection() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [audience, setAudience] = useState("all");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const load = useCallback(() => { api.get("/admin/broadcasts").then((r) => setHistory(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const AUDIENCES = [
    { key: "all", label: "Everyone" },
    { key: "pro", label: "Pro only" },
    { key: "free", label: "Free only" },
  ];

  const send = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Add a title and a message"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast", {
        title: title.trim(), body: body.trim(),
        link: link.trim() || null, link_label: linkLabel.trim() || null, audience,
      });
      toast.success(`Sent to ${data.recipients} member${data.recipients === 1 ? "" : "s"}`);
      setTitle(""); setBody(""); setLink(""); setLinkLabel(""); setAudience("all");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send broadcast");
    } finally { setSending(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/broadcasts/${id}`); setHistory((p) => p.filter((x) => x.id !== id)); toast.success("Announcement deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const inputCls = "w-full bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-4 py-2.5 text-white placeholder:text-[#6b7280] focus:outline-none focus:border-[#FF5A00] transition-colors";

  return (
    <div className="mb-10 bg-[#15151d] border border-[#2a3040] rounded-xl p-5" data-testid="broadcast-section">
      <h2 className="font-display font-black text-2xl tracking-tight mb-1 flex items-center gap-2">
        <Megaphone size={20} className="text-[#FF5A00]" /> Send a broadcast
      </h2>
      <p className="text-[#9ca3af] text-sm mb-4">
        Notify members with an in-app announcement (bell icon) and a push notification to everyone who opted in.
      </p>

      <div className="space-y-3">
        <input data-testid="broadcast-title" className={inputCls} placeholder="Title (e.g. New feature just dropped)"
          value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
        <textarea data-testid="broadcast-body" className={inputCls} rows={3} maxLength={1000}
          placeholder="Your message to the community…" value={body} onChange={(e) => setBody(e.target.value)} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input data-testid="broadcast-link" className={inputCls} placeholder="Optional link (https://… or /app)"
            value={link} onChange={(e) => setLink(e.target.value)} />
          <input data-testid="broadcast-link-label" className={inputCls} placeholder="Button label (e.g. Read now)"
            value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-[#6b7280] font-bold mr-1">Send to</span>
          {AUDIENCES.map((a) => (
            <button key={a.key} data-testid={`audience-${a.key}`} onClick={() => setAudience(a.key)}
              className={`text-sm font-bold rounded-full px-3.5 py-1.5 border transition-colors ${
                audience === a.key ? "bg-[#FF5A00] text-[#0a0a0f] border-[#FF5A00]"
                                   : "bg-[#0a0a0f] text-[#9ca3af] border-[#2a3040] hover:text-white"}`}>
              {a.label}
            </button>
          ))}
        </div>

        <button data-testid="broadcast-send" onClick={send} disabled={sending}
          className="inline-flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-5 py-2.5 hover:bg-[#FF7A33] transition-colors disabled:opacity-60">
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {sending ? "Sending…" : "Send broadcast"}
        </button>
      </div>

      {history.length > 0 && (
        <div className="mt-6 pt-5 border-t border-[#2a3040]">
          <p className="text-xs uppercase tracking-wider text-[#6b7280] font-bold mb-3">Past broadcasts</p>
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="rounded-lg border border-[#2a3040] bg-[#0a0a0f] p-3" data-testid={`broadcast-item-${h.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-white">{h.title}</div>
                    <p className="text-[13px] text-[#9ca3af] whitespace-pre-wrap break-words">{h.body}</p>
                    <div className="text-[11px] text-[#6b7280] mt-1.5 flex flex-wrap gap-x-3">
                      <span className="text-[#FF5A00] font-bold uppercase">{h.audience}</span>
                      <span>{fmtDate(h.created_at)}</span>
                      {h.push_stats && <span>🔔 {h.push_stats.sent} pushed</span>}
                    </div>
                  </div>
                  <button data-testid={`broadcast-delete-${h.id}`} onClick={() => remove(h.id)}
                    className="shrink-0 text-[#6b7280] hover:text-[#f87171] p-1" title="Delete announcement">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function SecuritySection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/security").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendDigest = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/admin/security/send-digest");
      toast.success(`Digest emailed to ${res.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send digest");
    } finally { setSending(false); }
  };

  const LABEL = {
    malware_blocked: "Malware blocked",
    bad_file_blocked: "Unsafe file blocked",
    login_locked: "Login locked (brute force)",
    unauthorized_file: "Blocked file access",
  };
  const SEV = {
    high: "text-[#f87171] bg-[#f87171]/10 border-[#f87171]/30",
    medium: "text-[#FF5A00] bg-[#FF5A00]/10 border-[#FF5A00]/30",
    warning: "text-[#9ca3af] bg-[#2a3040] border-[#2a3040]",
  };
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  const s = data?.summary || {};
  const cards = [
    { icon: ShieldAlert, label: "Malware blocked (24h)", value: s.malware_24h ?? 0, danger: (s.malware_24h ?? 0) > 0 },
    { icon: AlertTriangle, label: "Unsafe files (24h)", value: s.blocked_files_24h ?? 0 },
    { icon: KeyRound, label: "Login lockouts (24h)", value: s.lockouts_24h ?? 0, danger: (s.lockouts_24h ?? 0) > 0 },
    { icon: Shield, label: "Events (7d)", value: s.total_7d ?? 0 },
  ];

  return (
    <section className="mb-10" data-testid="security-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield size={18} className="text-[#FF5A00]" />
          <h2 className="font-display font-bold text-xl text-white">Security</h2>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="security-send-digest" onClick={sendDigest} disabled={sending}
            className="flex items-center gap-1.5 text-xs font-medium text-[#0a0a0f] bg-[#FF5A00] hover:bg-[#FF7A33] rounded-lg px-3 py-1.5 transition-colors disabled:opacity-60">
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Email digest
          </button>
          <button data-testid="security-refresh" onClick={load}
            className="flex items-center gap-1.5 text-xs font-medium text-[#9ca3af] hover:text-white bg-[#15151d] border border-[#2a3040] rounded-lg px-3 py-1.5 transition-colors">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {cards.map((c, i) => (
          <div key={i} data-testid={`security-stat-${i}`}
            className={`rounded-xl border p-4 ${c.danger ? "border-[#f87171]/40 bg-[#f87171]/5" : "border-[#2a3040] bg-[#15151d]"}`}>
            <c.icon size={16} className={c.danger ? "text-[#f87171]" : "text-[#FF5A00]"} />
            <div className="text-2xl font-bold text-white mt-2">{c.value}</div>
            <div className="text-xs text-[#9ca3af]">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-[#15151d] border border-[#2a3040] rounded-xl overflow-hidden">
        {(!data || data.events.length === 0) ? (
          <div className="p-6 text-center text-sm text-[#9ca3af]" data-testid="security-empty">
            {data ? "All clear — no security events recorded. 🎉" : "Loading…"}
          </div>
        ) : (
          <div className="divide-y divide-[#2a3040] max-h-96 overflow-y-auto" data-testid="security-events">
            {data.events.map((e, i) => (
              <div key={i} data-testid={`security-event-${i}`} className="flex items-start gap-3 px-4 py-3">
                <span className={`shrink-0 text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded border ${SEV[e.severity] || SEV.warning}`}>
                  {e.severity}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white font-semibold">{LABEL[e.type] || e.type}</div>
                  <div className="text-xs text-[#9ca3af] break-words">{e.detail}</div>
                  <div className="text-[11px] text-[#6b7280] mt-0.5">
                    {fmt(e.created_at)}{e.ip ? ` · IP ${e.ip}` : ""}{e.user_email ? ` · ${e.user_email}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}


function CoOwnerSection() {
  const [list, setList] = useState([]);
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/co-owners").then((r) => setList(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const e = email.trim().toLowerCase();
    if (!e) { toast.error("Enter an email address"); return; }
    setAdding(true);
    try {
      const { data } = await api.post("/admin/co-owners", { email: e });
      toast.success(data.registered
        ? `${e} is now a co-owner`
        : `${e} will become a co-owner as soon as they sign up`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add co-owner");
    } finally { setAdding(false); }
  };

  const remove = async (e) => {
    setRemoving(e);
    try {
      await api.delete(`/admin/co-owners/${encodeURIComponent(e)}`);
      toast.success(`Removed co-owner access · ${e}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    } finally { setRemoving(null); }
  };

  return (
    <section className="mb-10" data-testid="co-owner-section">
      <div className="flex items-center gap-2 mb-1">
        <Crown size={16} className="text-[#FF5A00]" />
        <h2 className="font-display font-black text-xl text-white">Co-owners</h2>
      </div>
      <p className="text-sm text-[#9ca3af] mb-4">
        Grant another person full Owner Console access — same powers as you. Only you (the primary
        owner) can add or remove co-owners. If they haven't signed up yet, they'll be promoted
        automatically the moment they create their account.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input data-testid="co-owner-email-input" type="email" value={email}
          onChange={(e) => setEmail(e.target.value)} placeholder="co-owner@email.com"
          onKeyDown={(e) => e.key === "Enter" && add()}
          className="flex-1 bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-4 py-3 text-white placeholder:text-[#6b7280] focus:outline-none focus:border-[#FF5A00] focus:ring-1 focus:ring-[#FF5A00] transition-colors" />
        <button data-testid="co-owner-add-button" onClick={add} disabled={adding}
          className="inline-flex items-center justify-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-5 py-3 hover:bg-[#FF7A33] transition-colors disabled:opacity-60">
          {adding ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Add co-owner
        </button>
      </div>
      {list.length === 0 ? (
        <div className="bg-[#15151d] border border-[#2a3040] rounded-xl p-5 text-sm text-[#9ca3af]" data-testid="co-owner-empty">No co-owners yet.</div>
      ) : (
        <div className="space-y-2" data-testid="co-owner-list">
          {list.map((c) => (
            <div key={c.email} data-testid={`co-owner-${c.email}`}
              className="flex items-center justify-between gap-3 bg-[#15151d] border border-[#2a3040] rounded-xl px-4 py-3">
              <div className="min-w-0">
                <div className="text-white font-semibold truncate">{c.name || c.email}</div>
                {c.name && <div className="text-xs text-[#9ca3af] truncate">{c.email}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {c.active ? (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded font-bold bg-[#FF5A00] text-[#0a0a0f]"><Crown size={10} /> Active</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded font-bold bg-[#1a1a24] text-[#9ca3af]"><Clock size={10} /> Pending sign-up</span>
                )}
                <button data-testid={`co-owner-remove-${c.email}`} onClick={() => remove(c.email)} disabled={removing === c.email}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-[#9ca3af] hover:text-[#f87171] hover:bg-[#0a0a0f] transition-colors">
                  {removing === c.email ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


export default function OwnerPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [copiedPwd, setCopiedPwd] = useState(false);

  const load = useCallback((query = "") => {
    setLoading(true);
    api.get(`/admin/users${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then((r) => setUsers(r.data)).catch(() => toast.error("Could not load users")).finally(() => setLoading(false));
  }, []);

  const loadMetrics = useCallback(() => {
    api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.role !== "owner") { navigate("/"); return; }
    if (user?.role === "owner") loadMetrics();
  }, [user, navigate, loadMetrics]);

  useEffect(() => {
    if (user?.role !== "owner") return;
    const t = setTimeout(() => load(q), 350);
    return () => clearTimeout(t);
  }, [q, user, load]);

  const setPro = async (u, is_pro, days = null) => {
    setSavingId(u.id);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/pro`, { is_pro, days });
      setUsers((prev) => prev.map((x) => (x.id === u.id
        ? { ...x, is_pro: data.is_pro, pro_expires_at: data.pro_expires_at } : x)));
      if (is_pro) {
        const dlabel = days ? `for ${days} days` : "permanently";
        toast.success(`Granted Pro ${dlabel} · ${u.email}`);
      } else {
        toast.success(`Removed Pro · ${u.email}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update user");
    } finally {
      setSavingId(null);
    }
  };

  const openReset = (u) => {
    setResetTarget(u);
    setNewPassword("");
    setShowPassword(false);
    setCopiedPwd(false);
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$";
    let pwd = "";
    const arr = new Uint32Array(12);
    crypto.getRandomValues(arr);
    for (let i = 0; i < 12; i++) pwd += chars[arr[i] % chars.length];
    setNewPassword(pwd);
    setShowPassword(true);
    setCopiedPwd(false);
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(newPassword);
      setCopiedPwd(true);
      setTimeout(() => setCopiedPwd(false), 2000);
    } catch { toast.error("Could not copy"); }
  };

  const confirmReset = async () => {
    if (!resetTarget || newPassword.length < 6) return;
    setResetting(true);
    try {
      await api.post(`/admin/users/${resetTarget.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset · ${resetTarget.email}`);
      setResetTarget(null);
      setNewPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reset password");
    } finally {
      setResetting(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${toDelete.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== toDelete.id));
      toast.success(`Deleted ${toDelete.email}`);
      setToDelete(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete user");
    } finally {
      setDeleting(false);
    }
  };

  const proCount = users.filter((u) => u.is_pro).length;

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#0a0a0f]/80 border-b border-[#2a3040]"
        style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <button data-testid="owner-back-button" onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-[#9ca3af] hover:text-white transition-colors">
            <ArrowLeft size={18} /> Back
          </button>
          <Logo size="text-xl" />
        </div>
      </header>

      <motion.main initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto px-5 py-10" data-testid="owner-panel">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-3">
          <Crown size={14} /> Owner Console
        </div>
        <h1 className="font-display font-black text-4xl tracking-tight leading-none mb-2">Owner Dashboard</h1>
        <p className="text-[#9ca3af] mb-8">Metrics, members, and Pro access for <Brand />.</p>

        {user?.is_primary_owner && <CoOwnerSection />}

        <BroadcastSection />

        <SecuritySection />

        <ArticlesSection />

        <InviteSection />

        <SupportInbox />

        <ReportedPosts />

        <CommunityPromptSetter />

        {metrics && (
          <div className="mb-10" data-testid="owner-metrics">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <StatCard icon={Users} label="Total members" value={metrics.total_users}
                sub={`+${metrics.new_today} today · +${metrics.new_week} this week`} />
              <StatCard icon={Crown} label="Pro members" value={metrics.pro_users}
                sub={`${metrics.conversion_pct}% conversion`} accent />
              <StatCard icon={Activity} label="Active (7d)" value={metrics.active_week}
                sub={`${metrics.active_today} active today`} />
              <StatCard icon={MessageSquare} label="Messages today" value={metrics.messages_today}
                sub={`${metrics.messages_total} all-time`} />
              <StatCard icon={UserPlus} label="New this month" value={metrics.new_month}
                sub={`${metrics.free_users} free members`} />
              <StatCard icon={DollarSign} label="Revenue" value={`$${metrics.revenue_usd}`}
                sub={`${metrics.paid_subscriptions} paid subs`} accent />
              <StatCard icon={MessagesSquare} label="Conversations" value={metrics.conversations_total}
                sub="all-time" />
              <div className="bg-[#15151d] border border-[#2a3040] rounded-xl p-4 col-span-2 sm:col-span-1">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#6b7280] font-bold mb-2">
                  <TrendingUp size={13} className="text-[#FF5A00]" /> Trending topics
                </div>
                {metrics.top_topics.length ? (
                  <div className="flex flex-wrap gap-1">
                    {metrics.top_topics.map((t, i) => (
                      <span key={i} className="text-[10px] bg-[#1a1a24] text-[#c7ccd6] px-2 py-0.5 rounded-full">{t}</span>
                    ))}
                  </div>
                ) : <p className="text-xs text-[#6b7280]">No feed yet today</p>}
              </div>
            </div>
          </div>
        )}

        <h2 className="font-display font-black text-2xl tracking-tight mb-2">Manage Pro Access</h2>
        <p className="text-[#9ca3af] mb-5">
          Grant or revoke <Brand /> Pro for any member. <span className="text-white font-semibold">{proCount}</span> of{" "}
          <span className="text-white font-semibold">{users.length}</span> members are Pro.
        </p>

        <div className="relative mb-5">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b7280]" />
          <input data-testid="owner-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full bg-[#15151d] border border-[#2a3040] rounded-lg pl-11 pr-4 py-3 text-white placeholder:text-[#6b7280] focus:outline-none focus:border-[#FF5A00]" />
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 size={28} className="text-[#FF5A00] animate-spin" /></div>
        ) : users.length === 0 ? (
          <p className="text-center text-[#6b7280] py-16">No members found.</p>
        ) : (
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} data-testid={`owner-user-${u.id}`}
                className="flex items-center gap-3 bg-[#15151d] border border-[#2a3040] rounded-lg px-4 py-3">
                <div className="w-10 h-10 rounded-full bg-[#FF5A00] text-[#0a0a0f] font-black flex items-center justify-center shrink-0 font-display">
                  {(u.name || u.email || "?")[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-white truncate">{u.name || "Member"}</span>
                    <RoleBadge role={u.role} />
                  </div>
                  <div className="text-xs text-[#6b7280] truncate">{u.email}</div>
                </div>
                {u.is_pro && (
                  <span className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#FF5A00] font-bold whitespace-nowrap" data-testid={`owner-expiry-${u.id}`}>
                    <Clock size={11} /> {expiryLabel(u.pro_expires_at)}
                  </span>
                )}
                {u.role === "owner" ? (
                  <button disabled data-testid={`owner-toggle-pro-${u.id}`}
                    className="shrink-0 w-32 text-xs font-bold rounded-lg px-3 py-2 bg-[#1a1a24] text-[#6b7280] border border-[#2a3040] opacity-60 cursor-not-allowed">
                    Owner
                  </button>
                ) : u.is_pro ? (
                  <button data-testid={`owner-toggle-pro-${u.id}`} onClick={() => setPro(u, false)}
                    disabled={savingId === u.id}
                    className="shrink-0 w-32 text-xs font-bold rounded-lg px-3 py-2 bg-[#1a1a24] text-[#f87171] border border-[#2a3040] hover:border-[#f87171] transition-colors disabled:opacity-50">
                    {savingId === u.id ? "…" : "Revoke Pro"}
                  </button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`owner-toggle-pro-${u.id}`} disabled={savingId === u.id}
                        className="shrink-0 w-32 flex items-center justify-center gap-1 text-xs font-bold rounded-lg px-3 py-2 bg-[#FF5A00] text-[#0a0a0f] hover:bg-[#FF7A33] transition-colors disabled:opacity-50">
                        {savingId === u.id ? "…" : <>Grant Pro <ChevronDown size={13} /></>}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#15151d] border-[#2a3040] text-white min-w-[160px]">
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#6b7280]">Grant Pro for</DropdownMenuLabel>
                      <DropdownMenuSeparator className="bg-[#2a3040]" />
                      {DURATIONS.map((d) => (
                        <DropdownMenuItem key={d.label} data-testid={`owner-grant-${d.days || "perm"}-${u.id}`}
                          onClick={() => setPro(u, true, d.days)}
                          className="cursor-pointer focus:bg-[#1a1a24] focus:text-white gap-2">
                          <Clock size={13} className="text-[#FF5A00]" /> {d.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {u.role !== "owner" && u.role !== "admin" && (
                  <button data-testid={`owner-reset-password-${u.id}`} onClick={() => openReset(u)}
                    title="Reset password"
                    className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-[#6b7280] hover:text-[#FF5A00] hover:bg-[#1a1a24] border border-transparent hover:border-[#FF5A00]/40 transition-colors">
                    <KeyRound size={16} />
                  </button>
                )}
                {u.role !== "owner" && u.role !== "admin" && (
                  <button data-testid={`owner-delete-${u.id}`} onClick={() => setToDelete(u)}
                    title="Delete user"
                    className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-[#6b7280] hover:text-[#f87171] hover:bg-[#1a1a24] border border-transparent hover:border-[#f87171]/40 transition-colors">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </motion.main>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#15151d] border-[#2a3040] text-white" data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete this user?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9ca3af]">
              This permanently deletes <span className="text-white font-semibold">{toDelete?.email}</span> and all
              of their data — conversations, to-dos, calendar events, and billing records. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button"
              className="bg-[#1a1a24] border-[#2a3040] text-white hover:bg-[#22222c] hover:text-white">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button" onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-[#f87171] text-[#0a0a0f] hover:bg-[#ef4444] font-bold">
              {deleting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <AlertDialogContent className="bg-[#15151d] border-[#2a3040] text-white" data-testid="reset-password-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display flex items-center gap-2">
              <KeyRound size={18} className="text-[#FF5A00]" /> Reset password
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#9ca3af]">
              Set a new password for <span className="text-white font-semibold">{resetTarget?.email}</span>.
              This also clears any "too many attempts" lockout so they can sign in right away.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3">
            <label className="text-xs uppercase tracking-wider text-[#6b7280] font-bold">New password</label>
            <div className="relative">
              <input
                data-testid="reset-password-input"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                autoComplete="new-password"
                className="w-full bg-[#0a0a0f] border border-[#2a3040] rounded-lg pl-4 pr-20 py-3 text-white placeholder:text-[#6b7280] focus:outline-none focus:border-[#FF5A00]" />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {newPassword && (
                  <button type="button" data-testid="reset-password-copy" onClick={copyPassword}
                    title="Copy" className="p-1.5 text-[#6b7280] hover:text-white transition-colors">
                    {copiedPwd ? <Check size={16} className="text-[#4ade80]" /> : <Copy size={16} />}
                  </button>
                )}
                <button type="button" data-testid="reset-password-toggle" onClick={() => setShowPassword((s) => !s)}
                  title={showPassword ? "Hide" : "Show"} className="p-1.5 text-[#6b7280] hover:text-white transition-colors">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button type="button" data-testid="reset-password-generate" onClick={generatePassword}
              className="inline-flex items-center gap-2 text-xs font-bold text-[#FF5A00] hover:text-[#FF7A33] transition-colors">
              <RefreshCw size={13} /> Generate secure password
            </button>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-cancel-button"
              className="bg-[#1a1a24] border-[#2a3040] text-white hover:bg-[#22222c] hover:text-white">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction data-testid="reset-confirm-button"
              onClick={(e) => { e.preventDefault(); confirmReset(); }}
              disabled={resetting || newPassword.length < 6}
              className="bg-[#FF5A00] text-[#0a0a0f] hover:bg-[#FF7A33] font-bold disabled:opacity-50">
              {resetting ? "Saving…" : "Set new password"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
